import React, { useMemo, useState } from 'react';
import { Box, getDuration, useDebounce } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import ClaimStatusSelector from '../ClaimStatusSelector/ClaimStatusSelector';
import EmployerSelector from '../EmployerSelector';
import ExportButton from '../ExportButton';
import { useIsEmployerIsPresent, useMultipleEmployerSelectAvailable } from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import { ReportSummaryBase, ReportSummaryWrapper } from '../ReportSummary';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import ClaimTypeSelector from './ClaimTypeSelector/ClaimTypeSelector';
import { ClaimsActivitySummaryReportParams } from './ClaimsActivityReport.types';
import { useClaimsActivityReportExportSummaryCSVQuery, useClaimsActivityReportSummaryQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: ClaimsActivitySummaryReportParams;
  onSetFilterValue: FilterSetters<ClaimsActivitySummaryReportParams>;
}

const ClaimsActivityReportSummary: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);

  const debouncedClaimTypes = useDebounce(filtersValues.claimTypes, getDuration({ seconds: 0.5 }));
  const debouncedClaimStatuses = useDebounce(filtersValues.claimStatuses, getDuration({ seconds: 0.5 }));
  const {
    isLoading,
    data,
    lastTimeUpdateDate,
    title,
    isEmptyData,
    isLoadingMore,
    isHasNextPage,
    handleLoadMore,
  } = useClaimsActivityReportSummaryQuery({
    employerId: filtersValues.employerId,
    isMultipleEmployer: isMultipleEmployerAvailable,
    employerIds: filtersValues.employerIds,
    claimTypes: debouncedClaimTypes,
    claimStatuses: debouncedClaimStatuses,
    startDate: filtersValues.startDate,
    endDate: filtersValues.endDate,
  });

  const { handleExport: handleExportCSV, isLoading: isExportLoading } =
    useClaimsActivityReportExportSummaryCSVQuery({
      employerId: filtersValues.employerId,
      isMultipleEmployer: isMultipleEmployerAvailable,
      employerIds: filtersValues.employerIds,
      claimTypes: filtersValues.claimTypes,
      claimStatuses: filtersValues.claimStatuses,
      startDate: filtersValues.startDate,
      endDate: filtersValues.endDate,
    });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }
    if (!isLoading && !data.length) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }

    return (
      <ReportSummaryBase
        isPreviewMode={isPreviewMode}
        subTitle={title}
        isSmallMode
        isLoading={isLoading}
        data={data}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        isLoadingMore={isLoadingMore}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filtersValues.employerId, filtersValues.employerIds, isLoading, isPreviewMode, title]);
  return (
    <TopButtonPortalWrapper
      isPreviewMode={isPreviewMode}
      infoNode={
        <ReportLastUpdateDate
          isLoading={isLoading}
          key={`${filtersValues.employerId}`}
          value={lastTimeUpdateDate}
        />
      }
      buttonNode={
        <Box direction="row">
          <AddScheduledReportButton
            employerId={filtersValues.employerId}
            employerIds={filtersValues.employerIds}
            isMultipleEmployer={isMultipleEmployerAvailable}
            type={ReportTypeKeys.ClaimsActivity}
            tab={ReportTab.Summary}
          />
          <Box margin={{ left: 'spacing12' }}>
            <ExportButton
              onClick={handleExportCSV}
              isLoading={isExportLoading}
              disabled={!isEmployerIsPresent || isEmptyData}
              text="Download Summary (CSV)"
            />
          </Box>
        </Box>
      }
    >
      <ReportSummaryWrapper
        isPreviewMode={isPreviewMode}
        filters={
          <Box fill direction="row">
            <Box fill>
              {isMultipleEmployerAvailable ? (
                <MultipleEmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  values={filtersValues.employerIds}
                  onChangeValues={onSetFilterValue.employerIds}
                />
              ) : (
                <EmployerSelector
                  disabled={isPreviewMode && !!defaultEmployerId}
                  onSetAutoFocus={setAutoFocus}
                  isAutoFocus={isAutoFocus}
                  value={filtersValues.employerId}
                  onChangeValue={onSetFilterValue.employerId}
                />
              )}
            </Box>

            <Box fill margin={{ left: 'spacing12' }}>
              <ClaimStatusSelector
                disabled={!isEmployerIsPresent}
                value={filtersValues.claimStatuses}
                onChangeValue={onSetFilterValue.claimStatuses}
              />
            </Box>
            <Box fill margin={{ left: 'spacing12' }}>
              <ClaimTypeSelector
                disabled={!isEmployerIsPresent}
                value={filtersValues.claimTypes}
                onChangeValue={onSetFilterValue.claimTypes}
              />
            </Box>
          </Box>
        }
      >
        {childrenNode}
      </ReportSummaryWrapper>
    </TopButtonPortalWrapper>
  );
};

export default ClaimsActivityReportSummary;
